export const INGEST_ROW_KIND = "ingest_task";
export const INGEST_READ_PERMISSION = "airline_schedule_ingest:read";
export const INGEST_WRITE_PERMISSION = "airline_schedule_ingest:write";
export const OFFICIAL_READ_PERMISSION = "schedule_official:read";
export const OFFICIAL_PAGE_WORKSPACE_QUERY = "workspace";
export const OFFICIAL_WORKSPACE = "official";
export const INGEST_WORKSPACE = "ingest";

export function resolveOfficialPageWorkspace({
    canIngestRead,
    canOfficialRead,
    requestedWorkspace,
    hasOfficialDeepLink = false,
}) {
    if (canIngestRead && canOfficialRead) {
        if (hasOfficialDeepLink) {
            return OFFICIAL_WORKSPACE;
        }
        return requestedWorkspace === INGEST_WORKSPACE ? INGEST_WORKSPACE : OFFICIAL_WORKSPACE;
    }
    if (canIngestRead) {
        return INGEST_WORKSPACE;
    }
    if (canOfficialRead) {
        return OFFICIAL_WORKSPACE;
    }
    return null;
}

export const INGEST_STATUS_MAP = {
    running: { status: "processing", text: "识别中" },
    pending_review: { status: "warning", text: "待确认" },
    failed: { status: "error", text: "失败" },
    written_draft: { status: "default", text: "已写入草稿" },
    ready_to_publish: { status: "success", text: "已进入待发布" },
    discarded: { status: "default", text: "已丢弃" },
};

export function isIngestRow(record) {
    return Boolean(record) && record.row_kind === INGEST_ROW_KIND;
}

export function ingestRowKey(record) {
    return isIngestRow(record) ? `ingest:${record.id}` : record.id;
}

export function ingestAircraftRegs(record) {
    const fromField = Array.isArray(record?.aircraft_regs) ? record.aircraft_regs.filter(Boolean) : [];
    if (fromField.length) {
        return [...new Set(fromField)];
    }
    const fromDraft = [];
    const seen = new Set();
    (record?.draft_flights || []).forEach((row) => {
        const reg = row?.aircraft_reg;
        if (!reg || seen.has(reg)) return;
        seen.add(reg);
        fromDraft.push(reg);
    });
    return fromDraft;
}

export function ingestAircraftLabel(record) {
    return ingestAircraftRegs(record).join("、");
}

export function paperDiffLabel(canonical, paper) {
    const left = String(paper || "").trim();
    const right = String(canonical || "").trim();
    if (!left || !right || left === right) {
        return "";
    }
    return `纸面 ${left} → 名册 ${right}`;
}

export function unmatchedAirportCode(reason) {
    const match = String(reason || "").match(/机场\s+([A-Z0-9]{3,4})\s+主数据未对上/i);
    return match ? match[1].toUpperCase() : "";
}

export function shortUncertainLabel(reason) {
    const text = String(reason || "").trim();
    if (!text) return "需核对";
    const airport = unmatchedAirportCode(text);
    if (airport) return `${airport} 不在名册`;
    const hasEtd = /ETD/.test(text);
    const hasEta = /ETA/.test(text);
    if (hasEtd && hasEta) return "起降时刻看不清";
    if (hasEta) return "到达时刻看不清";
    if (hasEtd) return "起飞时刻看不清";
    return "需核对";
}

export function countUncertainRows(rows) {
    return (rows || []).filter((row) => Boolean(row?.uncertain)).length;
}

export function fieldNeedsAttention(row, field) {
    if (!row?.uncertain) return false;
    const reason = row.uncertain_reason || "";
    if (field === "std_utc") return /ETD/.test(reason);
    if (field === "sta_utc") return /ETA/.test(reason);
    const airport = unmatchedAirportCode(reason);
    if (!airport) return false;
    if (field === "departure_airport") return String(row.departure_airport || "").toUpperCase() === airport;
    if (field === "arrival_airport") return String(row.arrival_airport || "").toUpperCase() === airport;
    return false;
}

export function formatUtcDisplay(value) {
    if (!value) return "";
    const text = String(value).trim();
    if (!text) return "";
    return `${text.replace("T", " ").replace("Z", "")} UTC`;
}

export function hasIngestSource(record) {
    const info = record?.additional_info;
    return Boolean(info && typeof info === "object" && info.airline_schedule_ingest);
}

export function shouldKeepPaperRotations(record) {
    return hasIngestSource(record);
}

export function applyFlightRowPatch(row, patch) {
    const next = { ...(row || {}), ...(patch || {}) };
    const keys = Object.keys(patch || {});
    const selectOnly = keys.length === 1 && keys[0] === "selected";
    if (!selectOnly && keys.length > 0) {
        return { ...next, uncertain: false, uncertain_reason: "" };
    }
    return next;
}

export function hasSelectedFlights(rows) {
    return (rows || []).some((row) => Boolean(row?.selected));
}

export function rotationBandTitle(rotationNo) {
    const text = String(rotationNo || "").trim();
    return text ? `ROT ${text}` : "未标轮班";
}

export function groupFlightsByRotation(rows) {
    const groups = [];
    const indexByKey = {};
    (rows || []).forEach((row) => {
        const rotationNo = String(row?.rotation_no || "").trim();
        const key = rotationNo || "__empty__";
        if (indexByKey[key] == null) {
            indexByKey[key] = groups.length;
            groups.push({ rotation_no: rotationNo, rows: [] });
        }
        groups[indexByKey[key]] = {
            ...groups[indexByKey[key]],
            rows: [...groups[indexByKey[key]].rows, row],
        };
    });
    return groups;
}

export function resetOriginListPagination(pagination) {
    return { ...(pagination || {}), current: 1 };
}
